import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { Composition } from '../lib/entries.ts'
import { ArtefactIcon } from './ArtefactIcon.tsx'

/**
 * One catalog card — a topic bundle or a bespoke tool — linking to its own
 * route. Shared by every listing so they can't drift apart.
 *
 * `eyebrow` labels the card with whatever distinguishes it in the listing it
 * sits in: the department on the dashboard, the Quality Area on a department
 * page where every card shares one department already.
 *
 * `meta` carries the one-line summary (e.g. "QA2 · 13 documents" or "Tool") in
 * place of a raw tag dump; `composition` renders the document mix of a bundle;
 * `footer` remains free for anything more bespoke, like the dashboard's
 * "last used" line.
 *
 * The Draft chip sits in the eyebrow row rather than under the title so a draft
 * card is exactly as tall as an approved one — mixed listings used to come out
 * a chip-height ragged.
 */
export function ToolCard({
  id,
  to,
  department,
  eyebrow,
  title,
  isDraft,
  meta,
  composition,
  description,
  footer,
}: {
  id: string
  to?: string          // defaults to the bespoke tool route, preserving existing call sites
  department: string
  eyebrow?: string     // defaults to `department`, preserving existing call sites
  title: string
  isDraft?: boolean
  meta?: string
  composition?: Composition[]
  description?: string // what this card gets you, for cards with no composition
  footer?: ReactNode
}) {
  const total = composition?.reduce((n, c) => n + c.count, 0) ?? 0

  return (
    <Link to={to ?? `/tools/${id}`} className="card">
      <div className="card__head">
        <p className="card__dept">{eyebrow ?? department}</p>
        {isDraft && <span className="chip chip--draft">Draft</span>}
        <span className="card__icon" aria-hidden="true">
          <ArtefactIcon department={department} />
        </span>
      </div>

      <h2 className="card__title">{title}</h2>

      <div className="card__foot">
        {/* The bar is proportion at a glance; the legend beneath is the same
            information as text, so the bar itself is hidden from assistive tech
            rather than repeating it. A single segment has no proportion to
            show — it would be a coloured rule pretending to be a chart. */}
        {composition && composition.length > 1 && (
          <span className="comp" aria-hidden="true">
            {composition.map((c) => (
              <span
                key={c.type}
                className={`comp__seg comp__seg--${c.type}`}
                style={{ flexGrow: c.count }}
              />
            ))}
          </span>
        )}
        {composition && composition.length > 0 && (
          <p className="card__mix">
            {total} document{total === 1 ? '' : 's'}
            {/* A bundle filtered to a single type already says what it holds. */}
            {composition.length > 1 &&
              ` · ${composition.map((c) => `${c.count} ${c.label}`).join(', ')}`}
          </p>
        )}
        {description && <p className="card__mix">{description}</p>}
        {meta && <p className="card__when">{meta}</p>}
        {footer}
      </div>
    </Link>
  )
}
